"use client";

import { useQuery } from "@tanstack/react-query";
import { useP2PStore } from "@shared/src/stores/useP2PStore";
import { Network, Token, Currency, PaymentMethod } from "@sdk/shared/types/config.types";
import { useHostStore } from "../stores/useHostStore";

// Define props with optional flags for each data type
export interface P2PConfigProps {
  tokens?: boolean;
  networks?: boolean;
  currencies?: boolean;
  paymentMethods?: boolean;
}

// Define the base shape of all possible return data
interface BaseP2PConfigData {
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

// Define the complete data type with all properties
interface CompleteP2PConfigData extends BaseP2PConfigData {
  networks: Network[] | null;
  tokens: Token[] | null;
  currencies: Currency[] | null;
  paymentMethods: PaymentMethod[] | null;
}

// Create conditional return type based on input props
type P2PConfigData<T extends P2PConfigProps = object> = BaseP2PConfigData &
  (T extends { networks: true } ? { networks: Network[] | null } : object) &
  (T extends { tokens: true } ? { tokens: Token[] | null } : object) &
  (T extends { currencies: true } ? { currencies: Currency[] | null } : object) &
  (T extends { paymentMethods: true } ? { paymentMethods: PaymentMethod[] | null } : object);

// Function overload signatures for different prop combinations
export function useP2PConfig(): CompleteP2PConfigData;
export function useP2PConfig<T extends P2PConfigProps>(props: T): P2PConfigData<T>;

// Implementation
export function useP2PConfig<T extends P2PConfigProps>(props?: T): P2PConfigData<T> | CompleteP2PConfigData {
  const sdk = useHostStore(state => state.sdk);
  const configService = sdk?.config;
  const {
    networks: storeNetworks,
    tokens: storeTokens,
    currencies: storeCurrencies,
    paymentMethods: storePaymentMethods,
  } = useP2PStore();

  // If no props are provided, fetch all data
  const fetchAll = !props;
  const fetchNetworks = Boolean(configService && (fetchAll || props?.networks));
  const fetchTokens = Boolean(configService && (fetchAll || props?.tokens));
  const fetchCurrencies = Boolean(configService && (fetchAll || props?.currencies));
  const fetchPaymentMethods = Boolean(configService && (fetchAll || props?.paymentMethods));

  // Check if we need to fetch any data
  const shouldFetchNetworks = fetchNetworks && !storeNetworks;
  const shouldFetchTokens = fetchTokens && !storeTokens;
  const shouldFetchCurrencies = fetchCurrencies && !storeCurrencies;
  const shouldFetchPaymentMethods = fetchPaymentMethods && !storePaymentMethods;

  // Fetch networks if needed
  const networksQuery = useQuery({
    queryKey: ["networks"],
    queryFn: () => configService?.fetchNetworks(),
    enabled: shouldFetchNetworks,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch tokens if needed
  const tokensQuery = useQuery({
    queryKey: ["tokens"],
    queryFn: () => configService?.fetchTokens(),
    enabled: shouldFetchTokens,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch currencies if needed
  const currenciesQuery = useQuery({
    queryKey: ["currencies"],
    queryFn: () => configService?.fetchCurrencies(),
    enabled: shouldFetchCurrencies,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch payment methods if needed
  const paymentMethodsQuery = useQuery({
    queryKey: ["paymentMethods"],
    queryFn: () => configService?.fetchPaymentMethods(),
    enabled: shouldFetchPaymentMethods,
    staleTime: 5 * 60 * 1000,
  });

  // Determine loading and error states based on what we're fetching
  const isLoading = Boolean(
    (shouldFetchNetworks && networksQuery.isLoading) ||
      (shouldFetchTokens && tokensQuery.isLoading) ||
      (shouldFetchCurrencies && currenciesQuery.isLoading) ||
      (shouldFetchPaymentMethods && paymentMethodsQuery.isLoading),
  );

  const isError = Boolean(
    (shouldFetchNetworks && networksQuery.isError) ||
      (shouldFetchTokens && tokensQuery.isError) ||
      (shouldFetchCurrencies && currenciesQuery.isError) ||
      (shouldFetchPaymentMethods && paymentMethodsQuery.isError),
  );

  // Collect all errors
  const error = networksQuery.error || tokensQuery.error || currenciesQuery.error || paymentMethodsQuery.error || null;

  // Build the result object based on what was requested
  const result: CompleteP2PConfigData = {
    isLoading,
    isError,
    error,
    networks: null,
    tokens: null,
    currencies: null,
    paymentMethods: null,
  };

  if (fetchNetworks) {
    result.networks = shouldFetchNetworks ? networksQuery.data?.data ?? null : storeNetworks ?? null;
  }

  if (fetchTokens) {
    result.tokens = shouldFetchTokens ? tokensQuery.data?.data ?? null : storeTokens ?? null;
  }

  if (fetchCurrencies) {
    result.currencies = shouldFetchCurrencies ? currenciesQuery.data?.data ?? null : storeCurrencies ?? null;
  }

  if (fetchPaymentMethods) {
    result.paymentMethods = shouldFetchPaymentMethods
      ? paymentMethodsQuery.data?.data ?? null
      : storePaymentMethods ?? null;
  }

  return result;
}
